import CloseIcon from '@mui/icons-material/Close'
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Stack, styled, Typography } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { CarrierType, ContainerDto, NonNumericOrderDto, OrderResponseDto } from '@planning/app/api'
import { PaginatedList } from '@planning/components/PaginatedList'
import {
  NnrOrderContainerAssignment,
  NnrOrderContainerAssignmentPaginationStore,
} from '@planning/rt-stores/nnrOrder/NnrOrderContainerAssignmentPaginationStore'
import nnrOrderService from '@planning/services/nnrOrderService'
import { useTranslate } from '@tolgee/react'
import { ContainerJourneyCard } from '@tom-ui/utils'
import { useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useMemo } from 'react'
import { AssignContainerListCard } from '../../molecules/AssignContainerListCard'
import {
  ContainerRequirements,
  ContainerRequirementsStack,
} from '../../molecules/ContainerRequirementsStack'

interface Props {
  nnrOrder: NonNumericOrderDto
  orderId?: number
  order?: OrderResponseDto
  isOpen: boolean
  onClose: () => void
}

const DialogWithDividers = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    borderTop: `1px solid ${theme.palette.grey[300]}`,
  },
}))

const canRenderJourneyCard = (container: ContainerDto) => {
  return !!container.id && !!container.height && !!container.type
}

const mapContainerForJourneyCard = (container: ContainerDto) => {
  if (!canRenderJourneyCard(container)) return undefined

  return {
    id: container.id!,
    number: container.number,
    isoCode: container.isoCode,
    operator: container.operator,
    length: container.length,
    height: container.height!,
    type: container.type!,
    isOnTerminal: !!container.isOnTerminal,
    isEmpty: !!container.isEmpty,
    damages: [],
    unitType: container.unitType,
    maxGross: container.maxGross,
    tare: container.tare,
    plateNumber: container.plateNumber,
  }
}

const mapOrderForJourneyCard = (order: OrderResponseDto, fallbackCarrierType: CarrierType) => ({
  id: order.id,
  customerName: order.customerName,
  carrierVisitType: order.carrierVisitType ?? fallbackCarrierType,
  railcarName: order.waggon,
  isEmpty: order.isEmpty,
  imoClasses: order.imoClasses,
  isDangerous: order.isDangerous,
  grossWeight: order.grossWeight,
  vgm: order.vgm,
  referenceNumber: order.referenceNumber,
})

export const NonNumericReleaseAssignOrderDialog: FC<Props> = observer(
  ({ nnrOrder, orderId, order, isOpen, onClose }) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()

    const { appViewStore } = usePlanningStore()

    const nnrOrderContainerAssignmentViewStore = useMemo(
      () => new NnrOrderContainerAssignmentPaginationStore(nnrOrder.id),
      [nnrOrder.id],
    )
    useEffect(() => {
      if (isOpen) {
        nnrOrderContainerAssignmentViewStore.loadCurrentPage()
      } else {
        // Timeout to allow dialog close animation to finish before resetting store
        setTimeout(() => nnrOrderContainerAssignmentViewStore.setFilter(), 300)
      }
    }, [nnrOrderContainerAssignmentViewStore, isOpen])

    const noContainersAvailable = () => {
      return (
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          justifyContent='center'
          p={theme.customSpacing.xl}
        >
          <Typography>
            {t(
              'looksLikeThereAreNoContainersThatFitYourRequirements',
              'Looks like there are no containers that fit your requirements',
            )}
          </Typography>
        </Box>
      )
    }

    const handleAssign = async (container: ContainerDto) => {
      if (!container.id) return

      try {
        await nnrOrderService.assignContainer({
          nonNumericOrderId: nnrOrder.id,
          containerId: container.id,
          orderId,
        })

        appViewStore.setShowAlert('success', 'Container added')
        onClose()
      } catch {
        appViewStore.setShowAlert('error', 'Failed to add Container')
      }
    }

    const suggestedContainerDto = useMemo(() => {
      if (!order?.suggestedEmptyContainerId) return null

      return {
        id: order.suggestedEmptyContainerId,
        number: order.suggestedEmptyContainerNumber,
        isoCode: order.suggestedEmptyContainerIsoCode,
      } as ContainerDto
    }, [
      order?.suggestedEmptyContainerId,
      order?.suggestedEmptyContainerIsoCode,
      order?.suggestedEmptyContainerNumber,
    ])

    const renderAssignmentOption = (assignment: NnrOrderContainerAssignment) => {
      const journeyCardContainer = mapContainerForJourneyCard(assignment.container)

      if (!assignment.containerJourney || !journeyCardContainer) {
        return (
          <AssignContainerListCard
            key={`assign-container-list-item-${assignment.container.id}`}
            container={assignment.container}
            onAssign={() => handleAssign(assignment.container)}
          />
        )
      }

      return (
        <ContainerJourneyCard
          key={`assign-container-journey-item-${assignment.containerJourney.id}`}
          container={journeyCardContainer}
          inbound={
            assignment.inboundOrder &&
            mapOrderForJourneyCard(assignment.inboundOrder, nnrOrder.modeOfTransport)
          }
          outbound={
            assignment.outboundOrder &&
            mapOrderForJourneyCard(assignment.outboundOrder, nnrOrder.modeOfTransport)
          }
          secondaryActionLabel={t('assign', 'Assign')}
          onSecondaryAction={() => handleAssign(assignment.container)}
        />
      )
    }

    if (!nnrOrder) {
      return null
    }

    const containerRequirements: ContainerRequirements = {
      operatorName: nnrOrder.shippingLine,
      archetypeName: nnrOrder.archetypeName,
      containerLengths: nnrOrder.containerLengths,
      containerHeights: nnrOrder.containerHeights,
      containerTypes: nnrOrder.containerTypes,
    }

    const hasContainersToAssign =
      nnrOrderContainerAssignmentViewStore.totalCount > 0 ||
      !!nnrOrderContainerAssignmentViewStore.filter

    return (
      <DialogWithDividers open={isOpen} onClose={onClose} maxWidth='md' fullWidth scroll='body'>
        <DialogTitle>
          <Stack direction='row' alignItems='center' justifyContent='space-between'>
            {t('assignContainer', 'Assign container')}
            <IconButton
              aria-label='close'
              onClick={onClose}
              sx={{
                color: theme => theme.palette.text.primary,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant='subtitle2' mb={2} mt={2}>
            {t('containerRequirements', 'Container requirements')}
          </Typography>
          <Stack
            spacing={theme.customSpacing.m}
            direction='column'
            display='flex'
            alignItems='center'
          >
            <ContainerRequirementsStack
              containerRequirements={containerRequirements}
              width='100%'
              hideTitle
            />

            {!hasContainersToAssign && !suggestedContainerDto ? (
              noContainersAvailable()
            ) : (
              <>
                {suggestedContainerDto && (
                  <AssignContainerListCard
                    container={suggestedContainerDto}
                    onAssign={() => handleAssign(suggestedContainerDto)}
                    suggested
                  />
                )}

                {hasContainersToAssign && (
                  <>
                    {suggestedContainerDto && (
                      <Typography variant='subtitle2' mb={2}>
                        {t('otherOptions', 'Other options')}
                      </Typography>
                    )}
                    <PaginatedList
                      paginatedStore={nnrOrderContainerAssignmentViewStore}
                      hidePaginationControl
                      filterable
                      searchPlaceholderText={t(
                        'searchByContainerNumber',
                        'Search by container number',
                      )}
                      noPadding
                    >
                      {nnrOrderContainerAssignmentViewStore.pageItems.length > 0 && (
                        <Stack spacing={theme.customSpacing.xs} pt={theme.customSpacing.l} width='100%'>
                          {nnrOrderContainerAssignmentViewStore.pageItems.map(renderAssignmentOption)}
                        </Stack>
                      )}
                    </PaginatedList>
                  </>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
      </DialogWithDividers>
    )
  },
)
